import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/firebase';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import LoadingSpinner from '@/shared/components/LoadingSpinner';
import { logger } from '@/utils/logger';
import { safeReturnPath } from '@/utils/redirectSecurity';

const EmailVerificationPending: React.FC = () => {
  const { currentUser, refreshUserData, logOut } = useEnhancedAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const [isSwitchingAccount, setIsSwitchingAccount] = useState(false);

  const safeRedirect = safeReturnPath(searchParams.get('redirect'));

  useEffect(() => {
    if (!currentUser) {
      navigate('/signin', { replace: true });
      return;
    }

    if (currentUser.emailVerified) {
      const encoded = encodeURIComponent(safeRedirect);
      navigate(`/account-setup?redirect=${encoded}`, { replace: true });
    }
  }, [currentUser, navigate, safeRedirect]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleResendVerification = async () => {
    if (!currentUser || resendCooldown > 0) {
      return;
    }

    setIsResending(true);
    try {
      const sendCustomVerificationEmail = httpsCallable(functions, 'sendCustomVerificationEmail');
      await sendCustomVerificationEmail({});
      setStatusMessage('Verification email sent. Please check your inbox and spam folder.');
      setResendCooldown(60);
      logger.info('EmailVerificationPending', 'Verification email resent via SendGrid', { userId: currentUser.uid });
    } catch (error) {
      logger.error('EmailVerificationPending', 'Failed to send verification email', { error });
      setStatusMessage('We could not send the verification email. Please try again in a moment.');
    } finally {
      setIsResending(false);
    }
  };

  const handleCheckStatus = async () => {
    if (!currentUser) return;

    setIsCheckingStatus(true);
    try {
      await currentUser.reload();
      await refreshUserData();

      if (currentUser.emailVerified) {
        setStatusMessage('Great! Your email is verified. Redirecting you now...');
        const encoded = encodeURIComponent(safeRedirect);
        setTimeout(() => {
          navigate(`/account-setup?redirect=${encoded}`, { replace: true });
        }, 1500);
      } else {
        setStatusMessage('We still show your email as unverified. Click the link in your inbox, then choose “Check again”.');
      }
    } catch (error) {
      logger.error('EmailVerificationPending', 'Failed to check verification status', { error });
      setStatusMessage('We could not confirm your verification status. Please try again.');
    } finally {
      setIsCheckingStatus(false);
    }
  };

  const handleUseDifferentEmail = async () => {
    if (isSwitchingAccount) return;

    setIsSwitchingAccount(true);
    try {
      await logOut();
      navigate('/signin', { replace: true });
    } catch (error) {
      logger.error('EmailVerificationPending', 'Failed to sign out before switching email', { error });
      setStatusMessage('We could not sign you out. Please try again.');
    } finally {
      setIsSwitchingAccount(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4">
      <div className="max-w-lg w-full bg-neutral-100 rounded-2xl shadow-ledge-lg p-8">
        <div className="flex flex-col items-center text-center space-y-4">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-18 8h18" />
            </svg>
          </div>

          <h1 className="text-2xl font-bold text-neutral-900">Check your inbox</h1>
          <p className="text-neutral-600">
            We sent a verification link to <span className="font-semibold">{currentUser.email}</span>. Please verify your email to unlock your 7 free days and dashboard access.
          </p>

          {import.meta.env.VITE_USE_EMULATOR === 'true' && (
            <div className="text-xs text-primary-600 bg-primary-50 px-3 py-2 rounded-md">
              Emulator mode: check the emulator logs for the verification link.
            </div>
          )}

          {statusMessage && (
            <div className="w-full px-4 py-3 text-sm bg-primary-50 text-primary-700 rounded-md">
              {statusMessage}
            </div>
          )}

          <div className="w-full space-y-3">
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={isResending || resendCooldown > 0}
              className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? 'Sending…' : resendCooldown > 0 ? `Resend email in ${resendCooldown}s` : 'Resend verification email'}
            </button>

            <button
              type="button"
              onClick={handleCheckStatus}
              disabled={isCheckingStatus}
              className="w-full px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCheckingStatus ? 'Checking…' : "I've verified, check again"}
            </button>

            <button
              type="button"
              onClick={handleUseDifferentEmail}
              disabled={isSwitchingAccount}
              className="w-full px-4 py-2 text-sm text-neutral-500 hover:text-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSwitchingAccount ? 'Signing out…' : 'Use a different email'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPending;
